import { api } from './api';

export type AdminLicenseSelection =
  | 'free'
  | 'commercial'
  | 'template_pack'
  | 'professional'
  | 'agency'
  | 'reseller'
  | 'bundle'
  | 'fastpass';

// Dashboard Stats
export const getDashboardStats = async () => {
  const response = await api.get('/api/admin/dashboard/stats', {
    params: { ts: Date.now() },
    headers: { 'Cache-Control': 'no-cache' },
  });
  return response.data;
};

// User Management
export const getUsers = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  accountType?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}) => {
  const response = await api.get('/api/admin/users', { params });
  return response.data;
};

export const getUserById = async (userId: string) => {
  const response = await api.get(`/api/admin/users/${userId}`);
  return response.data;
};

export const createUser = async (data: {
  email: string;
  password?: string;
  fullName?: string;
  companyName?: string;
  license?: AdminLicenseSelection;
  accountType?: string;
  credits?: number;
}) => {
  const response = await api.post('/api/admin/users/create', data);
  return response.data;
};

export const updateUser = async (userId: string, updates: any) => {
  const response = await api.put(`/api/admin/users/${userId}`, updates);
  return response.data;
};

export const resetUserPassword = async (userId: string, newPassword: string) => {
  const response = await api.post(`/api/admin/users/${userId}/reset-password`, { newPassword });
  return response.data;
};

export const deleteUser = async (userId: string) => {
  const response = await api.delete(`/api/admin/users/${userId}`);
  return response.data;
};

export const suspendUser = async (userId: string, reason?: string) => {
  const response = await api.post(`/api/admin/users/${userId}/suspend`, { reason });
  return response.data;
};

export const activateUser = async (userId: string) => {
  const response = await api.post(`/api/admin/users/${userId}/activate`);
  return response.data;
};

export interface ImpersonateUserResponse {
  url: string;
  accessToken?: string;
  refreshToken?: string;
  user?: {
    id: string;
    email: string;
    fullName?: string | null;
  };
}

export const impersonateUser = async (userId: string) => {
  const response = await api.post<ImpersonateUserResponse>(`/api/admin/users/${userId}/impersonate`);
  return response.data;
};

// Subscription Management
export const getUserSubscription = async (userId: string) => {
  const response = await api.get(`/api/admin/users/${userId}/subscription`);
  return response.data;
};

// License & Credits Management
export const setUserLicense = async (userId: string, license: AdminLicenseSelection) => {
  const response = await api.post(`/api/admin/users/${userId}/license`, { license });
  return response.data;
};

export const creditUser = async (userId: string, amount: number, reason?: string) => {
  const response = await api.post(`/api/admin/users/${userId}/credits`, { amount, reason });
  return response.data;
};

export const updateUserSubscription = async (userId: string, updates: {
  accountType?: string;
  expiresAt?: string;
  monthlyLimit?: number;
}) => {
  const response = await api.put(`/api/admin/users/${userId}/subscription`, updates);
  return response.data;
};

export const extendSubscription = async (userId: string, days: number) => {
  const response = await api.post(`/api/admin/users/${userId}/subscription/extend`, { days });
  return response.data;
};

export const cancelSubscription = async (userId: string, reason?: string) => {
  const response = await api.post(`/api/admin/users/${userId}/subscription/cancel`, { reason });
  return response.data;
};

export const addBonusGenerations = async (userId: string, amount: number) => {
  const response = await api.post(`/api/admin/users/${userId}/generations/add`, { amount });
  return response.data;
};

// User Activity & Stats
export const getUserActivity = async (userId: string, limit: number = 50) => {
  const response = await api.get(`/api/admin/users/${userId}/activity`, { params: { limit } });
  return response.data;
};

export const getUserGenerations = async (userId: string, page: number = 1, limit: number = 20) => {
  const response = await api.get(`/api/admin/users/${userId}/generations`, { params: { page, limit } });
  return response.data;
};

export const getUserStats = async (userId: string, days: number = 30) => {
  const response = await api.get(`/api/admin/users/${userId}/stats`, { params: { days } });
  return response.data;
};

// Generation Statistics
export const getGenerationStats = async (days: number = 30) => {
  const response = await api.get('/api/admin/generations/stats', { params: { days } });
  return response.data;
};

export const getTemplateUsageStats = async (days: number = 30) => {
  const response = await api.get('/api/admin/generations/templates', { params: { days } });
  return response.data;
};

// Audit Log
export const getAuditLog = async (params: {
  page?: number;
  limit?: number;
  adminId?: string;
  targetUserId?: string;
  actionType?: string;
}) => {
  const response = await api.get('/api/admin/audit-log', { params });
  return response.data;
};

export const getWebhookLogs = async (params: {
  page?: number;
  limit?: number;
  provider?: string;
  status?: string;
  search?: string;
}) => {
  const response = await api.get('/api/admin/webhook-logs', { params });
  return response.data;
};

export const replayWebhookLog = async (webhookLogId: string) => {
  const response = await api.post(`/api/admin/webhook-logs/${webhookLogId}/replay`);
  return response.data;
};

// Bulk Operations
export const exportUsers = async (filters?: {
  accountType?: string;
  status?: string;
}) => {
  const response = await api.post('/api/admin/users/bulk/export', filters || {}, {
    responseType: 'blob',
  });
  return response.data;
};

export const bulkNotifyUsers = async (userIds: string[], subject: string, message: string) => {
  const response = await api.post('/api/admin/users/bulk/notify', { userIds, subject, message });
  return response.data;
};

// Email Templates
export const getEmailTemplates = async () => {
  const response = await api.get('/api/admin/emails');
  return response.data;
};

export const updateEmailTemplate = async (templateId: string, updates: { subject: string, body_html: string }) => {
  const response = await api.put(`/api/admin/emails/${templateId}`, updates);
  return response.data;
};
